import { splitEarnings } from "./earningsCalc";
import { getPlatformFeePercent, getPlatformFeeRate } from "./liberFees";
import { isPaymentReleasedNotification } from "./paymentReleasedNotification";

/** Resolve gross milestone amount from milestone row + linked gig payment. */
export function resolveMilestoneGross(row) {
  const sources = [
    row?.gig_payment?.amount,
    row?.project_gig_payment?.amount,
    row?.payment?.amount,
    row?.amount,
    row?.project_milestone_for_freelancer?.amount,
  ];
  for (const raw of sources) {
    const s = String(raw ?? "").trim();
    if (!s) continue;
    const n = Number(s.replace(/[^\d.-]/g, ""));
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

export function mapServerNotificationRow(row) {
  return {
    id: row.id,
    title: row.title,
    message: row.body,
    body: row.body,
    type: row.type,
    metadata: row.metadata,
    url: row.url,
    time: row.created_at ? new Date(row.created_at).toLocaleString() : "",
    source: "server",
  };
}

/** Refresh header bells after server-side events (reviews, payments, etc.). */
export function dispatchNotificationsRefresh() {
  window.dispatchEvent(new CustomEvent("liber-freelancer-notifications"));
  window.dispatchEvent(new CustomEvent("liber-business-notifications"));
}

/**
 * Freelancer notifications (scoped per user id). Revision requests are merged
 * from polling needs_revision milestones + optional legacy liber_notifications.
 */

const revisionSeenKey = (userId) =>
  `liber_freelancer_revision_seen_v1_${userId}`;
const freelancerNotifStorageKey = (userId) =>
  `liber_notifications_freelancer_${userId}`;
const releasedSeenKey = (userId) =>
  `liber_freelancer_released_seen_v1_${userId}`;
const releasedNotifStorageKey = (userId) =>
  `liber_notifications_freelancer_released_${userId}`;

function milestoneRevisionStamp(row) {
  const t = row?.updated_at ?? row?.updatedAt;
  if (t == null) return "";
  try {
    return new Date(t).toISOString();
  } catch {
    return String(t);
  }
}

function parseSeen(userId) {
  try {
    const raw = localStorage.getItem(revisionSeenKey(userId));
    const o = raw ? JSON.parse(raw) : {};
    return o && typeof o === "object" && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
}

function writeSeen(userId, obj) {
  const keys = Object.keys(obj);
  if (keys.length > 60) {
    const trimmed = {};
    keys.slice(-60).forEach((k) => {
      trimmed[k] = obj[k];
    });
    localStorage.setItem(revisionSeenKey(userId), JSON.stringify(trimmed));
    return;
  }
  localStorage.setItem(revisionSeenKey(userId), JSON.stringify(obj));
}

export function readFreelancerRevisionNotifications(userId) {
  if (userId == null) return [];
  try {
    const raw = localStorage.getItem(freelancerNotifStorageKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function readFreelancerPaymentReleasedNotifications(userId) {
  if (userId == null) return [];
  try {
    const raw = localStorage.getItem(releasedNotifStorageKey(userId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function writeFreelancerRevisionNotifications(userId, items) {
  if (userId == null) return;
  localStorage.setItem(
    freelancerNotifStorageKey(userId),
    JSON.stringify(items),
  );
}

function writeFreelancerPaymentReleasedNotifications(userId, items) {
  if (userId == null) return;
  localStorage.setItem(releasedNotifStorageKey(userId), JSON.stringify(items));
}

export function dispatchFreelancerNotificationsUpdated() {
  window.dispatchEvent(new CustomEvent("liber-freelancer-notifications"));
}

/** Legacy key used by FreelancerSubmitRequest success toast. */
export function readLegacyFreelancerNotifications() {
  try {
    const raw = localStorage.getItem("liber_notifications");
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * New needs_revision milestones (per milestone id + updated stamp) become bell items.
 */
export function syncRevisionRequestAlertsFromRows(rows, userId) {
  if (userId == null || !Array.isArray(rows)) {
    return { added: 0 };
  }

  const seen = parseSeen(userId);
  const notifs = readFreelancerRevisionNotifications(userId);

  let added = 0;

  for (const row of rows) {
    const id = Number(row?.id);
    if (!Number.isFinite(id)) continue;

    const stamp = milestoneRevisionStamp(row);
    const key = String(id);
    const dedupeVal = stamp || "__pending_ts__";

    if (seen[key] === dedupeVal) continue;

    seen[key] = dedupeVal;

    const projectName =
      row?.project_milestone_for_freelancer?.project_gig_details
        ?.project_name || "your project";
    const remarks = String(row?.remarks || "").trim();
    const preview =
      remarks.length > 140 ? `${remarks.slice(0, 137)}…` : remarks;

    notifs.unshift({
      title: "Revision requested",
      message: `The client asked for changes on "${projectName}".${preview ? ` Latest note: ${preview}` : " Open Projects to resubmit your milestone."}`,
      url: "/projects",
      time: new Date().toLocaleString(),
      milestoneId: id,
      revisionStamp: dedupeVal,
    });
    added += 1;
  }

  writeSeen(userId, seen);
  writeFreelancerRevisionNotifications(userId, notifs.slice(0, 25));
  return { added };
}

/**
 * Approved milestones become "payment released" bell items (deduped per milestone id + updated stamp).
 */
/** Backfill amounts on cached payment-released bell items (fixes older AED 0.00 entries). */
export function repairReleasedNotificationAmounts(rows, userId) {
  if (userId == null || !Array.isArray(rows) || !rows.length) return false;
  const notifs = readFreelancerPaymentReleasedNotifications(userId);
  const byId = new Map(
    rows
      .filter((r) => String(r?.status ?? "").toLowerCase() === "approved")
      .map((r) => [Number(r.id), r]),
  );
  let changed = false;
  const repaired = notifs.map((n) => {
    const row = byId.get(Number(n.milestoneId));
    if (!row) return n;
    const gross = resolveMilestoneGross(row);
    if (!(gross > 0)) return n;
    if (Number(n.metadata?.milestone_amount) > 0) return n;
    changed = true;
    const { platform_fee, net } = splitEarnings(gross);
    const feePct = getPlatformFeePercent();
    const projectName =
      row?.project_milestone_for_freelancer?.project_gig_details?.project_name ||
      "your project";
    return {
      ...n,
      message: [
        `Your milestone for "${projectName}" was approved.`,
        "",
        `Milestone Amount: AED ${gross.toFixed(2)}`,
        `Liber Service Fee (${feePct}%): - AED ${platform_fee.toFixed(2)}`,
        `Amount Received: AED ${net.toFixed(2)}`,
        "",
        "View in Payments.",
      ].join("\n"),
      metadata: {
        ...n.metadata,
        kind: "payment_released",
        currency: "AED",
        milestone_amount: gross,
        service_fee: platform_fee,
        amount_received: net,
        fee_rate: getPlatformFeeRate(),
      },
    };
  });
  if (changed) {
    writeFreelancerPaymentReleasedNotifications(userId, repaired);
  }
  return changed;
}

export function syncPaymentReleasedAlertsFromRows(rows, userId) {
  if (userId == null || !Array.isArray(rows)) {
    return { added: 0 };
  }

  let seen = {};
  try {
    const raw = localStorage.getItem(releasedSeenKey(userId));
    const parsed = raw ? JSON.parse(raw) : {};
    seen =
      parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : {};
  } catch {
    seen = {};
  }

  const notifs = readFreelancerPaymentReleasedNotifications(userId);
  let added = 0;

  for (const row of rows) {
    if (String(row?.status ?? "").toLowerCase() !== "approved") continue;
    const id = Number(row?.id);
    if (!Number.isFinite(id)) continue;
    const stamp = milestoneRevisionStamp(row) || "__approved__";
    const key = String(id);
    if (seen[key] === stamp) continue;
    seen[key] = stamp;

    const projectName =
      row?.project_milestone_for_freelancer?.project_gig_details
        ?.project_name || "your project";
    const gross = resolveMilestoneGross(row);
    const { platform_fee, net } = splitEarnings(gross);
    const feePct = getPlatformFeePercent();

    notifs.unshift({
      title: "Payment released",
      message: [
        `Your milestone for "${projectName}" was approved.`,
        "",
        `Milestone Amount: AED ${gross.toFixed(2)}`,
        `Liber Service Fee (${feePct}%): - AED ${platform_fee.toFixed(2)}`,
        `Amount Received: AED ${net.toFixed(2)}`,
        "",
        "View in Payments.",
      ].join("\n"),
      metadata: {
        kind: "payment_released",
        currency: "AED",
        milestone_amount: gross,
        service_fee: platform_fee,
        amount_received: net,
        fee_rate: getPlatformFeeRate(),
      },
      url: "/payments",
      time: new Date().toLocaleString(),
      milestoneId: id,
      approvedStamp: stamp,
    });
    added += 1;
  }

  try {
    localStorage.setItem(releasedSeenKey(userId), JSON.stringify(seen));
  } catch {
    /* ignore */
  }
  writeFreelancerPaymentReleasedNotifications(userId, notifs.slice(0, 25));
  return { added };
}

export function mergeFreelancerBellItems(userId, serverRows = []) {
  const legacy = readLegacyFreelancerNotifications();
  if (userId == null) {
    return legacy.slice(0, 25);
  }
  const revision = readFreelancerRevisionNotifications(userId);
  const released = readFreelancerPaymentReleasedNotifications(userId);
  const local = [...revision, ...released, ...legacy];

  const server = (serverRows || []).map(mapServerNotificationRow);
  const serverReleasedMilestones = new Set(
    server
      .filter((n) => isPaymentReleasedNotification(n))
      .map((n) => String(n.metadata?.milestone_id ?? n.milestoneId ?? ""))
      .filter(Boolean),
  );

  const filteredLocal = local.filter((n) => {
    if (!isPaymentReleasedNotification(n)) return true;
    const mid = String(n.milestoneId ?? "");
    return !mid || !serverReleasedMilestones.has(mid);
  });

  return [...server, ...filteredLocal].slice(0, 30);
}
